import { GenericAbortSignal } from 'axios';

import {
  BulkPayerType,
  FacilityNetworkSummary,
  GroupPayerNetworkStatusIndex,
  NetworkStatus,
  NetworkStatusClientSummary,
  PayerNetworkSummary,
  StatusField,
} from 'models/GroupPayerNetworkStatus';
import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const groupPayerNetworkStatusQueryKeys = {
  base: ['groupPayerNetworkStatuses'],
  facilities: (params: Record<string, any> = {}) => ['groupPayerNetworkStatuses', 'facilities', params],
  payers: (params: Record<string, any> = {}) => ['groupPayerNetworkStatuses', 'payers', params],
  rows: (params: Record<string, any> = {}) => ['groupPayerNetworkStatuses', 'rows', params],
  clients: (params: Record<string, any> = {}) => ['groupPayerNetworkStatuses', 'clients', params],
};

// Undefined is the acting client's own settings (Configuration); a clientId is an Olio admin managing
// that client's. Every call takes it, and the query params carry it, so the two never share a cache entry.
export type ClientScoped = { clientId?: string };

export function clientBasePath(clientId?: string) {
  return clientId ? `/admin/clients/${clientId}` : '';
}

const path = (clientId?: string) => `${clientBasePath(clientId)}/group_payer_network_statuses`;

export type NetworkStatusFilter = { stediPayerId: string } | { groupId: string };

export type NewGroupPayerNetworkStatuses = { stediPayerIds: string[]; groupIds: string[] } & Record<
  StatusField,
  NetworkStatus
>;

export async function indexFacilityNetworkSummaries(
  { clientId, ...params }: ClientScoped & BaseIndexQueryParams & { stediPayerId?: string },
  signal: GenericAbortSignal
) {
  return http
    .get<Paginated<FacilityNetworkSummary>>(`${path(clientId)}/groups`, { params, signal })
    .then((response) => response.data);
}

export async function indexPayerNetworkSummaries(
  { clientId, ...params }: ClientScoped & BaseIndexQueryParams & { stediPayerIds?: string[] },
  signal: GenericAbortSignal
) {
  return http
    .get<Paginated<PayerNetworkSummary>>(`${path(clientId)}/payers`, { params, signal })
    .then((response) => response.data);
}

export async function destroyGroupPayerNetworkStatuses({ clientId, ...params }: ClientScoped & NetworkStatusFilter) {
  return http.delete(path(clientId), { params });
}

export async function indexGroupPayerNetworkStatuses(
  { clientId, ...params }: ClientScoped & NetworkStatusFilter & { stediPayerIds?: string[] } & BaseIndexQueryParams,
  signal: GenericAbortSignal
) {
  return http.get<GroupPayerNetworkStatusIndex>(path(clientId), { params, signal }).then((response) => response.data);
}

export async function updateGroupPayerNetworkStatus({
  id,
  clientId,
  ...statuses
}: ClientScoped & { id: string } & Partial<Record<StatusField, NetworkStatus>>) {
  return http.patch(`${path(clientId)}/${id}`, statuses);
}

export async function updateGroupPayerNetworkStatuses({
  clientId,
  ...body
}: ClientScoped & {
  ids: string[];
  payerType: BulkPayerType;
  status: NetworkStatus;
}) {
  return http.patch(path(clientId), body);
}

export async function destroyGroupPayerNetworkStatus({ id, clientId }: ClientScoped & { id: string }) {
  return http.delete(`${path(clientId)}/${id}`);
}

export async function createGroupPayerNetworkStatuses({
  clientId,
  ...body
}: ClientScoped & NewGroupPayerNetworkStatuses) {
  return http.post(path(clientId), body);
}

// Admin > Payer Network Status: every client, with when its settings last changed.
export async function adminIndexNetworkStatusClients(params: BaseIndexQueryParams, signal?: GenericAbortSignal) {
  return http
    .get<Paginated<NetworkStatusClientSummary>>('/admin/group_payer_network_statuses/clients', { params, signal })
    .then((response) => response.data);
}
